import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function NamedInputDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  initialValue = "",
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  busyLabel = "Saving…",
  onSubmit,
  validate,
  testId,
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setError("");
      return;
    }
    setValue(initialValue);
    setError("");
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, initialValue]);

  const submit = async (e) => {
    e?.preventDefault();
    const nextValue = value.trim();
    const nextError = validate ? validate(nextValue) : (!nextValue ? "Enter a name." : "");
    if (nextError) {
      setError(nextError);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(nextValue);
      onOpenChange(false);
    } catch (_e) {
      // Caller owns the toast/error surface.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!busy) onOpenChange(nextOpen); }}>
      <DialogContent
        data-testid={testId}
        className="sm:max-w-md bg-[hsl(var(--ploy-card))] border-[hsl(var(--ploy-border))]"
      >
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight text-[hsl(var(--ploy-ink))]">
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="text-[hsl(var(--ploy-ink-soft))]">
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--ploy-brass-deep))]">
              {label}
            </Label>
            <Input
              ref={inputRef}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError("");
              }}
              placeholder={placeholder}
              className="rounded-sm mt-1"
            />
            {error ? <p className="text-xs text-destructive mt-2">{error}</p> : null}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] hover:bg-[hsl(var(--ploy-ink))]/90 rounded-[10px] font-semibold"
            >
              {busy ? busyLabel : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
